import React, { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, Phone, Mail } from "lucide-react";
import logo from "../../assets/gobuildweb-logo.png";
import { CONTACT } from "../../data/site";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "What We Can Build", to: "/what-we-can-build" },
  { label: "Pricing", to: "/pricing" },
  { label: "Client Login", to: "/client-login" },
];

export default function Nav() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${scrolled ? "border-b border-white/10 bg-black/70 backdrop-blur-xl" : "bg-transparent"}`}
      data-testid="main-nav"
    >
      <div className="mx-auto flex h-[72px] max-w-[1200px] items-center justify-between px-5 md:px-8">
        <Link to="/" className="flex items-center" data-testid="nav-logo"><img src={logo} alt="GoBuildWeb" className="h-9 w-auto md:h-10" /></Link>
        <nav className="hidden items-center gap-1 lg:flex">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"}
              className={({ isActive }) => `px-3.5 py-2 text-sm font-medium transition ${isActive ? "text-gold-400" : "text-white/80 hover:text-white"}`}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-5">
          <div className="hidden xl:flex flex-col items-end leading-tight" data-testid="nav-contact">
            <a href={CONTACT.phoneHref} className="flex items-center gap-1.5 text-sm font-semibold text-white transition hover:text-gold-400" data-testid="nav-phone"><Phone size={13} className="text-gold-500" /> {CONTACT.phone}</a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-1.5 text-xs text-zinc-400 transition hover:text-gold-400" data-testid="nav-email"><Mail size={11} className="text-gold-500" /> {CONTACT.email}</a>
          </div>
          <a href={CONTACT.phoneHref} className="flex items-center gap-1.5 text-sm font-semibold text-white transition hover:text-gold-400 xl:hidden" data-testid="nav-phone-sm"><Phone size={14} className="text-gold-500" /> {CONTACT.phone}</a>
          <Link to="/get-started" className="inline-flex items-center gap-2 rounded-full bg-gold-500 px-5 py-2.5 text-sm font-semibold text-black transition hover:bg-gold-400" data-testid="nav-get-website">
            Let's Get Started <ArrowRight size={15} />
          </Link>
        </div>
        <button className="text-white lg:hidden" onClick={() => setOpen((v) => !v)} aria-label="Menu" data-testid="nav-mobile-toggle">{open ? <X size={26} /> : <Menu size={26} />}</button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-white/10 bg-black/95 lg:hidden">
            <div className="flex flex-col gap-1 px-5 py-5">
              {LINKS.map((l) => <button key={l.to} onClick={() => go(l.to)} className="py-2.5 text-left font-medium text-white/90" data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</button>)}
              <button onClick={() => go("/get-started")} className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-gold-500 px-5 py-3 font-semibold text-black" data-testid="nav-mobile-get-website">Let's Get Started <ArrowRight size={16} /></button>
              <div className="mt-4 flex flex-col gap-2 border-t border-white/10 pt-4" data-testid="nav-mobile-contact">
                <a href={CONTACT.phoneHref} className="flex items-center gap-2 text-sm font-semibold text-white"><Phone size={14} className="text-gold-500" /> {CONTACT.phone}</a>
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 text-sm text-zinc-400"><Mail size={14} className="text-gold-500" /> {CONTACT.email}</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
